import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { PlanTier, SubscriptionContextType } from './types';
import { PLAN_LIMITS, checkSubscriptionExpiring } from './constants';
import {
  checkAdminRole,
  syncAdminTier,
  fetchSubscriptionData,
  createStripeCheckout,
  createStripePortal,
  updateSubscriptionTier
} from './operations';

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [tier, setTierState] = useState<PlanTier>('Personal');
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [isTrial, setIsTrial] = useState(false);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const planLimits = useMemo(() => PLAN_LIMITS[tier], [tier]);

  // Reset to defaults when user logs out
  const resetToDefaults = useCallback(() => {
    setTierState('Free');
    setSubscribed(false);
    setSubscriptionEnd(null);
    setIsTrial(false);
    setTrialEnd(null);
    setIsLoading(false);
  }, []);

  const checkSubscription = useCallback(async (forceRefresh = false) => {
    if (!user) {
      resetToDefaults();
      return;
    }
    
    logger.info('[SubscriptionContext] Starting checkSubscription', { forceRefresh, userId: user.id });
    setIsLoading(true);
    
    try {
      // Check if user is admin
      const isAdmin = await checkAdminRole(user.id);
      
      if (isAdmin) {
        logger.info('[SubscriptionContext] Admin user detected');
        await syncAdminTier(user.id, user.email);
        
        setTierState('Pro');
        setSubscribed(true);
        setSubscriptionEnd(null);
        setIsTrial(false);
        setTrialEnd(null);
        setIsLoading(false);
        return;
      }

      const data = await fetchSubscriptionData(forceRefresh);
      
      if (data) {
        logger.info('[SubscriptionContext] Received subscription data:', data);
        
        setTierState(data.tier);
        setSubscribed(data.subscribed);
        setSubscriptionEnd(data.subscriptionEnd);
        setIsTrial(data.isTrial);
        setTrialEnd(data.trialEnd);
        
        // Show expiration warnings
        if (data.subscribed && data.subscriptionEnd && !forceRefresh) {
          const { isExpiring, daysUntilEnd, hasExpired } = checkSubscriptionExpiring(data.subscriptionEnd);
          
          if (isExpiring) {
            toast.warning(`Sua assinatura expira em ${daysUntilEnd} ${daysUntilEnd === 1 ? 'dia' : 'dias'}`);
          } else if (hasExpired) {
            toast.error('Sua assinatura expirou');
          }
        }
      } else {
        // If data is null (auth error or network issue), set defaults
        logger.warn('[SubscriptionContext] No subscription data received, using defaults');
        setTierState('Free');
        setSubscribed(true); // Free users are considered subscribed in freemium model
      }
    } catch (e) {
      logger.error('[SubscriptionContext] Unexpected error checking subscription:', e);
      // Don't show toast for auth errors (user likely logged out)
      // Just set defaults silently
      setTierState('Free');
      setSubscribed(true);
    } finally {
      setIsLoading(false);
    }
  }, [user, resetToDefaults]);

  const createCheckoutSession = useCallback(async (priceId: string) => {
    if (!user) {
      toast.error('Você precisa estar logado para assinar');
      return;
    }

    logger.info('[Subscription] Creating checkout session for price:', priceId);
    setIsLoading(true);
    
    try {
      const url = await createStripeCheckout(priceId);
      
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Erro: URL de pagamento não foi gerada');
      }
    } catch (e) {
      logger.error('[Subscription] Unexpected error creating checkout:', e);
      toast.error('Erro ao criar sessão de pagamento');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const createPortalSession = useCallback(async () => {
    if (!user) {
      toast.error('Você precisa estar logado');
      return;
    }

    logger.info('[Subscription] Creating portal session');
    setIsLoading(true);
    
    try {
      const url = await createStripePortal(`${window.location.origin}/configuracoes`);
      
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Erro: URL do portal não foi gerada');
      }
    } catch (e) {
      logger.error('[Subscription] Unexpected error creating portal:', e);
      toast.error('Erro ao acessar portal de assinatura');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const setTier = useCallback(async (newTier: PlanTier) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const success = await updateSubscriptionTier(user.id, user.email, newTier);
      if (success) {
        setTierState(newTier);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Initial load
  useEffect(() => {
    if (user) {
      checkSubscription();
    }
  }, [user, checkSubscription]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      checkSubscription();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const value: SubscriptionContextType = useMemo(() => ({
    tier,
    subscribed,
    isLoading,
    planLimits,
    subscriptionEnd,
    isTrial,
    trialEnd,
    createCheckoutSession,
    createPortalSession,
    checkSubscription,
    setTier,
    refreshSubscription: checkSubscription
  }), [
    tier,
    subscribed,
    isLoading,
    planLimits,
    subscriptionEnd,
    isTrial,
    trialEnd,
    createCheckoutSession,
    createPortalSession,
    checkSubscription,
    setTier
  ]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within a SubscriptionProvider');
  return ctx;
};
